import './src/styles/fonts.css'
import './src/styles/vendors/swiper-bundle.min.css'
import './src/styles/vendors/tailwind.scss'
import './src/styles/vendors/accessible-nprogress.scss'
import './src/styles/global/tokens.scss'
import './src/styles/global/typography.scss'
import './src/styles/global/layout.scss'

import { createRoot, hydrateRoot } from 'react-dom/client'
import { SessionProvider, UIProvider } from '@faststore/sdk'
import type { GatsbyBrowser } from 'gatsby'
import { datadogRum } from '@datadog/browser-rum-slim'
import { ModalProvider } from 'src/sdk/ui/modal'
import { AccountProvider } from 'src/components/account/context'
import { ToastProvider } from 'src/components/ui/ToastMessage/ToastContext'
import { ModalSheetProvider } from 'src/components/contexts/ModalSheetContext'
import initializeServiceChatBot from 'src/utils/initializeServiceChatBot'

import { releaseVersion } from './store.config'
import { getDatadogEnv } from './src/utils/runtimeEnvironment'
import {
  makeVirtualPageEvent,
  sendEvent,
} from './src/utils/restructure/analytics'
import { captureSessionAttribution } from './src/utils/sessionAttribution'
import Layout from './src/Layout'
import ErrorBoundary from './src/sdk/error/ErrorBoundary'
import TestProvider from './src/sdk/tests'
import { uiActions, uiEffects, uiInitialState } from './src/sdk/ui'

// Several components push straight to window.dataLayer; GTM only creates it on idle,
// so make sure it exists from the first render.
window.dataLayer = window.dataLayer ?? []

interface OnRouteUpdateProps {
  location: {
    pathname: string
    search: string
    hash: string
  }
  prevLocation: {
    pathname: string
    href: string
    origin: string
  }
}

export const onRouteUpdate = ({
  location,
  prevLocation,
}: OnRouteUpdateProps) => {
  captureSessionAttribution(location.search, document.referrer)

  if (window) {
    window.previousPath = prevLocation
  }

  if (window) {
    const event = makeVirtualPageEvent(window, prevLocation)

    sendEvent(event)
  }

  if (document) {
    initializeServiceChatBot(location.pathname)
  }

  if (
    process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'build'
  ) {
    const pagePath = location
      ? location.pathname + location.search + location.hash
      : undefined

    setTimeout(() => {
      if (typeof gtag === 'function') {
        gtag('event', 'page_view', { page_path: pagePath })
      }
    }, 100)
  }
}

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
  element,
}) => {
  return (
    <ErrorBoundary>
      <TestProvider>
        <UIProvider
          initialState={uiInitialState}
          actions={uiActions}
          effects={uiEffects}
        >
          <SessionProvider
            initialState={{
              channel: '{"salesChannel":"3"}',
              country: 'BRA',
              currency: { code: 'BRL', symbol: 'R$' },
            }}
          >
            <ModalSheetProvider>
              <ModalProvider>
                <ToastProvider>
                  <AccountProvider>{element}</AccountProvider>
                </ToastProvider>
              </ModalProvider>
            </ModalSheetProvider>
          </SessionProvider>
        </UIProvider>
      </TestProvider>
    </ErrorBoundary>
  )
}

// Diagnostic only: captures the component stack for hydration mismatches
// (React error #418/#423) so Datadog can attribute them to a component,
// since react-dom's own minified stack never reveals which one diverged.
const onHydrationRecoverableError = (error: unknown, ...rest: unknown[]) => {
  try {
    const errorInfo = rest[0] as { componentStack?: string } | undefined

    // eslint-disable-next-line no-console
    console.error(
      '[Hydration] onRecoverableError:',
      error,
      '\ncomponentStack:',
      errorInfo?.componentStack
    )

    datadogRum.addError(error, {
      context: 'hydration-recoverable-error',
      componentStack: errorInfo?.componentStack,
    })
  } catch {
    // diagnostics must never break hydration
  }
}

export const replaceHydrateFunction = (() => {
  return (
    element: React.ReactElement,
    container: Element,
    callback?: () => void
  ) => {
    try {
      if (container.hasChildNodes()) {
        hydrateRoot(container, element, {
          onRecoverableError: onHydrationRecoverableError,
        })
      } else {
        createRoot(container, {
          onRecoverableError: onHydrationRecoverableError,
        }).render(element)
      }
    } catch (error) {
      // hydrateRoot/createRoot failed outright: fall back to a plain client
      // render so the page still mounts instead of staying blank.
      try {
        datadogRum.addError(error, { context: 'hydration-fatal-error' })
      } catch {
        // diagnostics must never break hydration
      }

      createRoot(container).render(element)
    }

    callback?.()
  }
}) as unknown as GatsbyBrowser['replaceHydrateFunction']

export const onClientEntry = () => {
  if (typeof window !== 'undefined') {
    captureSessionAttribution(window.location.search, document.referrer)
  }

  const initDatadog = () => {
    try {
      const env = getDatadogEnv()

      datadogRum.init({
        applicationId: '780fbb82-88bf-498a-8663-99f22af1ff08',
        clientToken: 'pub157699f10de263b3c954dd8e800b78c6',
        site: 'datadoghq.eu',
        service: 'ecommerce-b2c-faststore',
        env,
        version: releaseVersion,
        sessionSampleRate: 1,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: (event) => {
          if (event.type === 'error') {
            const isThirdParty =
              event.error?.stack?.includes('googletagmanager') ||
              event.error?.stack?.includes('gtm') ||
              event.error?.source?.includes('googletagmanager')

            if (isThirdParty) {
              return false
            }
          }

          return true
        },
      })
    } catch (error) {
      console.error('Failed to initialize Datadog RUM:', error)
    }
  }

  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(initDatadog, { timeout: 5000 })
  } else {
    setTimeout(initDatadog, 1000)
  }
}

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
  props,
}) => {
  return <Layout {...props}>{element}</Layout>
}
